/**
 * Modal — Radix Dialog wrapper. ADR-0007 + Sprint 2 P0.
 * Source-of-truth для overlays блокирующих UI.
 *
 * Usage:
 *   <Modal open={open} onOpenChange={setOpen} title="..." description="...">
 *     <ModalBody>...</ModalBody>
 *     <ModalFooter>
 *       <Button variant="secondary" onClick={() => setOpen(false)}>Отмена</Button>
 *       <Button variant="primary" onClick={confirm}>OK</Button>
 *     </ModalFooter>
 *   </Modal>
 *
 * Для destructive confirmations используй <ConfirmationDialog/> из ConfirmationDialog.tsx.
 */
import * as Dialog from '@radix-ui/react-dialog'
import { type ReactNode } from 'react'
import { Icon } from '@/components/icons/Icon'

interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  children: ReactNode
  size?: 'sm' | 'md' | 'lg'
  hideClose?: boolean
}

const sizeMap = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl' } as const

export function Modal({ open, onOpenChange, title, description, children, size = 'md', hideClose }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-surface-overlay backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0"
        />
        <Dialog.Content
          className={`fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] ${sizeMap[size]} rounded-lg bg-surface-raised border border-border p-6 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 focus:outline-none`}
        >
          <Dialog.Title className="text-lg font-display font-semibold text-text-primary mb-1">{title}</Dialog.Title>
          {description && (
            <Dialog.Description className="text-sm text-text-secondary mb-4">{description}</Dialog.Description>
          )}
          <div className="text-sm text-text-primary">{children}</div>
          {!hideClose && (
            <Dialog.Close
              aria-label="Закрыть"
              className="absolute top-3 right-3 inline-flex items-center justify-center w-8 h-8 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-border-focus/40"
            >
              <Icon name="x" size={18} aria-hidden="true" />
            </Dialog.Close>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export function ModalBody({ children }: { children: ReactNode }) {
  return <div className="py-2">{children}</div>
}

export function ModalFooter({ children }: { children: ReactNode }) {
  return <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-border">{children}</div>
}
